<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="700px"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    transition="dialog-fade-transition"
    scrollable
    persistent
  >
    <v-form v-model="payvalid" ref="payform" @submit.prevent="addPayment">
      <v-card elevation="1">
        <v-card-title class="pa-2">
          <v-toolbar flat dense color="transparent" height="48">
            <v-toolbar-title class="checkout-title">
              <span class="gradient-text">Forma de Pago</span>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="closeDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>

        <v-card-text class="py-2 px-3">
          <!-- Información del cliente -->
          <v-row dense>
            <v-col cols="12">
              <div class="d-flex align-center mb-2">
                <h3 class="subtitle-1 font-weight-medium">
                  <v-icon left small>mdi-account</v-icon>
                  Información del cliente
                </h3>
              </div>

              <!-- Formulario directo del cliente -->
              <v-card outlined class="pa-2">
                <v-row dense>
                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="client.typeid"
                      :items="typeIdOptions"
                      item-value="code"
                      item-text="name"
                      label="Tipo de documento *"
                      outlined
                      dense
                      hide-details="auto"
                    >
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="8">
                    <v-text-field
                      v-model="client.PartyIdentification"
                      label="Número de documento *"
                      outlined
                      dense
                      hide-details="auto"
                      placeholder="Documento del cliente"
                      @keydown.enter.prevent="searchClientByDocument"
                    >
                      <template v-slot:append-outer>
                        <v-btn
                          icon
                          small
                          @click.stop.prevent="searchClientByDocument"
                          :disabled="!client.PartyIdentification"
                          :loading="searchingClient"
                          type="button"
                        >
                          <v-icon small>mdi-magnify</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="client.PartyName"
                      label="Nombre completo / Razón social *"
                      outlined
                      dense
                      hide-details="auto"
                      placeholder="Nombre del cliente"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="client.client_email"
                      label="Email"
                      outlined
                      dense
                      hide-details="auto"
                      placeholder="email@ejemplo.com"
                      type="email"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="client.client_phone"
                      label="Teléfono"
                      outlined
                      dense
                      hide-details="auto"
                      placeholder="Número de teléfono"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="8">
                    <v-text-field
                      v-model="client.AddressLine"
                      label="Dirección"
                      outlined
                      dense
                      hide-details="auto"
                      placeholder="Dirección del cliente"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-autocomplete
                      v-model="client.CityName"
                      :items="municipalitiesList"
                      item-text="name"
                      item-value="id"
                      label="Ciudad *"
                      outlined
                      dense
                      hide-details="auto"
                      placeholder="Buscar ciudad..."
                    >
                    </v-autocomplete>
                  </v-col>
                </v-row>
              </v-card>

              <!-- Validaciones del cliente -->
              <div v-if="clientValidationMessages.length > 0" class="mt-2">
                <v-alert
                  v-for="(message, index) in clientValidationMessages"
                  :key="index"
                  dense
                  type="warning"
                  text
                  class="mb-1"
                >
                  <v-icon left small>mdi-alert-circle</v-icon>
                  {{ message }}
                </v-alert>
              </div>
            </v-col>
          </v-row>

          <!-- Detalles del pago -->
          <v-row dense class="mt-2">
            <v-col cols="12">
              <h3 class="subtitle-1 font-weight-medium mb-2">
                <v-icon left small>mdi-cash-multiple</v-icon>
                Detalles del pago
              </h3>
            </v-col>

            <!-- Vendedor -->
            <v-col cols="12">
              <v-select
                v-model="vendor"
                :items="vendors"
                item-value="codigo"
                item-text="nombre"
                label="Vendedor *"
                outlined
                dense
                required
                hide-details="auto"
                @change="validateForm"
                :error="vendorError"
                :error-messages="vendorErrorMessage"
              >
                <template v-slot:prepend-inner>
                  <v-icon small>mdi-account-tie</v-icon>
                </template>
              </v-select>
            </v-col>

            <!-- Método de pago -->
            <v-col cols="12" md="6">
              <v-select
                v-model="pay_mode"
                :items="paymentMethods"
                return-object
                item-value="codigo"
                item-text="valor"
                label="Método de pago *"
                outlined
                dense
                hide-details="auto"
                class="mb-3"
                @change="onPaymentMethodChange"
              >
                <template v-slot:prepend-inner>
                  <v-icon small>{{ getPaymentMethodIcon() }}</v-icon>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-avatar size="24">
                    <v-icon small>{{ getPaymentMethodIcon(item) }}</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.valor }}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="payment.payment_reference"
                label="Referencia de pago"
                :error="referenceError"
                :error-messages="referenceErrorMessage"
                @input="validateReference"
                :required="!isEfectivo"
                outlined
                dense
                clearable
                hide-details="auto"
                class="mb-3"
                :hint="getReferenceHint()"
                persistent-hint
                :disabled="isEfectivo"
              >
                <template v-slot:prepend-inner>
                  <v-icon small>mdi-barcode</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <!-- Valor del pago -->
            <v-col cols="12" class="mt-2">
              <v-card outlined class="amount-card">
                <v-card-subtitle class="py-1 d-flex align-center">
                  <v-icon small left>mdi-cash-multiple</v-icon>
                  Valor del pago
                  <v-spacer></v-spacer>
                  <v-chip small outlined>
                    Máx: {{ formatCurrency(unpaidAmount) }}
                  </v-chip>
                </v-card-subtitle>
                <v-card-text class="py-2">
                  <v-row>
                    <v-col cols="12" sm="8">
                      <v-text-field
                        v-model.number="payment.payment_value"
                        @input="validatePaymentValue"
                        :error="amountError"
                        :error-messages="amountErrorMessage"
                        outlined
                        dense
                        hide-details="auto"
                        type="number"
                        step="0.01"
                        min="0.01"
                        clearable
                        :max="unpaidAmount"
                        :placeholder="formatCurrency(0)"
                      >
                        <template v-slot:prepend-inner>
                          <span class="text-body-2 grey--text">{{ currencySymbol }}</span>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-btn
                        block
                        outlined
                        color="primary"
                        @click="setFullAmount"
                        :disabled="unpaidAmount <= 0"
                      >
                        <v-icon left>mdi-calculator</v-icon>
                        Pago total
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Información del cambio para efectivo -->
              <div v-if="isEfectivo && showChangeInfo" class="mt-2">
                <v-alert dense type="info" text class="mb-0">
                  <div class="d-flex justify-space-between align-center">
                    <span>Cambio a entregar:</span>
                    <span class="font-weight-bold">{{
                      formatCurrency(changeAmount)
                    }}</span>
                  </div>
                </v-alert>
              </div>
            </v-col>
          </v-row>

          <!-- Resumen de pagos y vista previa -->
          <v-row
            dense
            v-if="payment.payment_value > 0 || hasPartialPayments"
            class="mt-2"
          >
            <v-col cols="12">
              <v-card outlined class="payment-preview">
                <v-card-title class="py-2 text-subtitle-1">
                  <v-icon left small>{{
                    hasPartialPayments ? "mdi-receipt-text" : "mdi-eye"
                  }}</v-icon>
                  {{ hasPartialPayments ? "Resumen de Pagos" : "Vista previa del pago" }}
                  <v-spacer></v-spacer>
                  <v-chip
                    v-if="payment.payment_value > 0"
                    small
                    :color="getPaymentStatusColor()"
                    text-color="white"
                  >
                    {{ getPaymentStatusText() }}
                  </v-chip>
                </v-card-title>
                <v-card-text class="py-2">
                  <!-- Resumen de pagos previos -->
                  <div v-if="hasPartialPayments" class="mb-2">
                    <div class="d-flex justify-space-between mb-1">
                      <span class="text-body-2">Total de la factura:</span>
                      <span class="font-weight-medium">{{
                        formatCurrency(totalAmount)
                      }}</span>
                    </div>
                    <div class="d-flex justify-space-between mb-1">
                      <span class="text-body-2">Ya pagado:</span>
                      <span class="font-weight-medium success--text">
                        {{ formatCurrency(totalAmount - unpaidAmount) }}
                      </span>
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <div class="d-flex justify-space-between mb-2">
                      <span class="font-weight-medium">Pendiente por pagar:</span>
                      <span class="font-weight-bold primary--text">
                        {{ formatCurrency(unpaidAmount) }}
                      </span>
                    </div>
                  </div>

                  <!-- Pago actual -->
                  <div v-if="payment.payment_value > 0">
                    <div v-if="hasPartialPayments" class="text-body-2 grey--text mb-2">
                      Pago actual:
                    </div>
                    <div class="d-flex justify-space-between align-center">
                      <div class="d-flex align-center">
                        <v-icon class="mr-2" color="primary">
                          {{ getPaymentMethodIcon() }}
                        </v-icon>
                        <div>
                          <div class="body-1 font-weight-medium">
                            {{ pay_mode.valor }}
                          </div>
                          <div
                            class="caption grey--text"
                            v-if="payment.payment_reference"
                          >
                            Ref: {{ payment.payment_reference }}
                          </div>
                        </div>
                      </div>
                      <div class="text-right">
                        <div class="display-1 font-weight-bold primary--text">
                          {{ formatCurrency(payment.payment_value) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pa-3">
          <v-btn text @click="closeDialog" class="mr-2" :disabled="processingPayment">
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            x-large
            type="submit"
            class="px-8 gradient-button"
            :disabled="!canProcessPayment"
            :loading="processingPayment"
          >
            <v-icon left>mdi-check-circle</v-icon>
            {{ getPaymentButtonText() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import createPayment from "../utils/createPayment.js";
import countryUtils from "../utils/countryUtils.js";
import { formatCurrency, webserver, fe_ws } from "@/services/webserver.js";

// Cliente por defecto para el público general
const CLIENTE_PUBLICO_GENERAL = {
  PartyName: "PÚBLICO EN GENERAL",
  PartyIdentification: "222222222222",
  typeid: "CC",
  client_email: "",
  client_phone: "",
  AddressLine: "",
  CityName: "",
};

export default {
  name: "CheckoutComponent",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    totalAmount: {
      type: Number,
      required: true,
    },
    unpaidAmount: {
      type: Number,
      required: true,
    },
    vendors: {
      type: Array,
      required: true,
    },
    paymentMethods: {
      type: Array,
      required: true,
    },
    idList: {
      type: Array,
      required: true,
    },
    municipalities: {
      type: Array,
      required: false,
      default: () => [],
    },
    companyData: {
      type: Object,
      required: true,
    },
    clientData: {
      type: Object,
      default: () => ({}),
    },
    validations: {
      type: Array,
      default: () => [
        { status: false, label: "Nombre del cliente requerido" },
        { status: true, label: "Correo electrónico es opcional" },
        { status: false, label: "Documento de identificación requerido" },
        { status: false, label: "Tipo de documento requerido" },
        { status: true, label: "Vendedor requerido" },
        { status: false, label: "Requiere Factura Electrónica" },
        { status: false, label: "Valor" },
        { status: false, label: "Ciudad requerida" },
      ],
    },
  },
  data() {
    return {
      dialogVisible: this.visible,
      clienteSolicitaFactura: false,
      montoRequiereFactura: false,
      doctype: "TK",
      payvalid: false,
      processingPayment: false,
      payment: createPayment(),
      pay_mode: {
        codigo: "01",
        valor: "EFECTIVO",
      },
      vendor: null,
      client: {},

      // Errores de validación
      vendorError: false,
      vendorErrorMessage: "",
      referenceError: false,
      referenceErrorMessage: "",
      amountError: false,
      amountErrorMessage: "",

      doctypes: [],
      searchingClient: false,
      municipalitiesList: [],
    };
  },

  created() {
    this.loadDocumentTypes();
    this.initializeComponent();
    this.get_colombia_municipalities();
  },

  computed: {
    typeIdOptions() {
      return this.idList || [];
    },
    hasPartialPayments() {
      return this.unpaidAmount < this.totalAmount;
    },

    canProcessPayment() {
      // Para efectivo, permitir pagos que excedan el monto (para dar cambio)
      const hasValidAmount = this.isEfectivo
        ? this.payment.payment_value > 0
        : this.payment.payment_value > 0 &&
          this.payment.payment_value <= this.unpaidAmount;

      const hasVendor = !!this.vendor;
      const hasValidReference =
        this.isEfectivo || !!this.payment.payment_reference?.trim();

      // Validar cliente (siempre requerido en Colombia)
      const clientValid = this.isValidClientForDocType();

      console.log("canProcessPayment check:", {
        hasValidAmount,
        hasVendor,
        hasValidReference,
        clientValid,
        requiresClientInfo: this.requiresClientInfo,
        doctype: this.doctype,
        isEfectivo: this.isEfectivo,
        payment_value: this.payment.payment_value,
        unpaidAmount: this.unpaidAmount,
      });

      return hasValidAmount && hasVendor && hasValidReference && clientValid;
    },

    isEfectivo() {
      return (
        this.pay_mode &&
        (this.pay_mode.codigo === "01" ||
          this.pay_mode.valor?.toLowerCase().includes("efectivo"))
      );
    },

    requiresClientInfo() {
      // En Colombia siempre se requiere información del cliente
      return true;
    },

    hasClientData() {
      // Clientes genéricos que no deben considerarse como datos reales del cliente
      const genericClientNames = [
        "PÚBLICO EN GENERAL",
        "CONSUMIDOR FINAL",
        "CLIENTE CONTADO",
        "CLIENTE EXTRANJERO",
      ];

      return (
        this.client &&
        this.client.PartyName &&
        !genericClientNames.includes(this.client.PartyName)
      );
    },

    canRequestFullInvoice() {
      const countryConfig = countryUtils.getCurrentCountrySettings();
      const countryCode = countryUtils.getCurrentCountryCode();

      // En Colombia, siempre FE sin restricciones de monto
      if (countryCode === "CO") {
        return true;
      }

      // En España, aplicar límites tradicionales
      const requiresFullInvoiceLimit = countryConfig.requiresFullInvoiceLimit || 400;
      return this.totalAmount <= requiresFullInvoiceLimit;
    },

    showDocTypeAlert() {
      const countryConfig = countryUtils.getCurrentCountrySettings();
      const countryCode = countryUtils.getCurrentCountryCode();

      // En Colombia, solo mostrar alerta para FC específicas
      if (countryCode === "CO") {
        return this.doctype === "FC" || this.clienteSolicitaFactura;
      }

      // En España, usar lógica original con límites
      const requiresFullInvoiceLimit = countryConfig.requiresFullInvoiceLimit || 400;
      return (
        this.doctype === "FC" ||
        this.totalAmount > requiresFullInvoiceLimit ||
        this.clienteSolicitaFactura
      );
    },

    changeAmount() {
      if (!this.isEfectivo || this.payment.payment_value <= this.unpaidAmount) {
        return 0;
      }
      return this.payment.payment_value - this.unpaidAmount;
    },

    showChangeInfo() {
      return this.isEfectivo && this.payment.payment_value > this.unpaidAmount;
    },

    clientValidationMessages() {
      const messages = [];

      if (!this.client.PartyName?.trim()) {
        messages.push("Nombre del cliente es requerido");
      }
      if (!this.client.PartyIdentification?.trim()) {
        messages.push("Documento de identificación es requerido");
      }
      if (!this.client.typeid?.trim()) {
        messages.push("Tipo de documento es requerido");
      }
      if (!this.client.CityName?.trim()) {
        messages.push("Ciudad es requerida");
      }

      return messages;
    },

    currencySymbol() {
      const countryConfig = countryUtils.getCurrentCountrySettings();
      return countryConfig.currencySymbol || "$";
    },
  },

  watch: {
    visible(newValue) {
      this.dialogVisible = newValue;
      if (newValue) {
        this.initializeComponent();
      }
    },

    dialogVisible(newValue) {
      if (!newValue) {
        this.$emit("update:visible", false);
      }
    },

    clientData: {
      handler(newValue) {
        console.log("🔄 Cliente recibido en checkout:", newValue);
        this.syncClientData(newValue);
      },
      immediate: true,
      deep: true,
    },

    totalAmount() {
      this.checkDocumentTypeByAmount();
    },

    unpaidAmount(newValue) {
      if (newValue > 0 && this.payment.payment_value === 0) {
        this.payment.payment_value = newValue;
      }
    },

    doctype() {
      this.validateForm();
    },
  },

  methods: {
    formatCurrency,

    get_colombia_municipalities() {
      const qry = {
        store: window.store.store_id,
        table: "settings",
        filters: [{ field: "tableName", operator: "=", value: "municipalities" }],
      };
      fe_ws(
        "get_table",
        qry,
        (data) => {
          console.log("Municipalities data received:", data);
          if (Array.isArray(data)) {
            this.municipalitiesList = data.map((m) => ({
              id: m.id,
              name: `${m.name} `,
            }));
          } else {
            console.error("Received non-array data for municipalities:", data);
            this.municipalitiesList = [];
          }
        },
        (error) => {
          console.error("Error fetching municipalities:", error);
          this.municipalitiesList = [];
        }
      );
    },

    get_rut(onSuccess, onNotFound) {
      // Convertir código de tipo de documento (CC, NIT, etc.) a ID numérico
      const docType = this.typeIdOptions.find(
        (item) => item.code === this.client.typeid
      );
      const typeDocId = docType ? parseInt(docType.id) : 1;

      var qry = {
        id_company: this.companyData.PartyIdentification,
        method: "POST",
        data: {
          environment: {
            type_environment_id: 1,
          },
          type_document_identification_id: typeDocId,
          identification_number: this.client.PartyIdentification,
        },
      };
      console.log("Consultando DIAN:", qry);
      fe_ws("get_acquirer", qry, (data) => {
        console.log("Respuesta DIAN:", data);
        try {
          var rut = JSON.parse(data);
          if (rut.name) {
            // Cliente encontrado en DIAN
            this.client.PartyName = rut.name;
            this.client.client_email = rut.email || "";
            if (onSuccess) onSuccess(rut);
          } else {
            // No encontrado en DIAN
            this.client.CityName = this.companyData.city_code || "";
            if (onNotFound) onNotFound();
          }
        } catch (e) {
          console.error("Error parseando respuesta DIAN:", e);
          if (onNotFound) onNotFound();
        }
      });
    },

    loadDocumentTypes() {
      this.doctypes = countryUtils.getInvoiceTypes();
    },

    initializeComponent() {
      this.syncClientData(this.clientData);
      this.initializePaymentValue();
      this.setInitialDocumentType();
      this.setInitialVendor();
    },

    syncClientData(newValue) {
      if (newValue && Object.keys(newValue).length > 0) {
        this.client = { ...newValue };
        // Si el cliente no tiene ciudad, usar la de la empresa
        if (!this.client.CityName) {
          this.client.CityName = this.companyData.city_code || "";
        }
        console.log("✅ Cliente sincronizado:", this.client);
      } else {
        this.client = { ...CLIENTE_PUBLICO_GENERAL };
        // Asignar siempre la ciudad de la empresa al cliente por defecto
        this.client.CityName = this.companyData.city_code || "";
        console.log("✅ Cliente por defecto establecido:", this.client);
      }

      this.$nextTick(() => {
        this.validateForm();
      });
    },

    initializePaymentValue() {
      this.payment.payment_value = this.unpaidAmount || 0;
    },

    setInitialDocumentType() {
      // Para Colombia, siempre Factura Electrónica
      this.doctype = "FE";
      this.emitDocTypeChange();
    },

    setInitialVendor() {
      if (this.vendors && this.vendors.length > 0 && !this.vendor) {
        this.vendor = this.vendors[0].codigo;
      }
    },

    closeDialog() {
      this.resetForm();
      this.dialogVisible = false;
      this.$emit("update:visible", false);
    },

    resetForm() {
      this.payment = createPayment();
      this.pay_mode = { codigo: "01", valor: "EFECTIVO" };
      this.vendor = null;
      this.clienteSolicitaFactura = false;
      this.processingPayment = false;
      this.clearErrors();
    },

    clearErrors() {
      this.vendorError = false;
      this.vendorErrorMessage = "";
      this.referenceError = false;
      this.referenceErrorMessage = "";
      this.amountError = false;
      this.amountErrorMessage = "";
    },

    // Métodos de validación
    isValidClientForDocType() {
      // Para Factura Electrónica en Colombia, validar datos básicos
      return (
        this.client.PartyName?.trim() &&
        this.client.PartyIdentification?.trim() &&
        this.client.typeid?.trim() &&
        this.client.typeid !== "GEN"
      );
    },

    validateForm() {
      this.clearErrors();
      let isValid = true;

      // Validar vendedor
      if (!this.vendor) {
        this.vendorError = true;
        this.vendorErrorMessage = "Debe seleccionar un vendedor";
        isValid = false;
      }

      // Validar referencia de pago
      if (!this.isEfectivo && !this.payment.payment_reference?.trim()) {
        this.referenceError = true;
        this.referenceErrorMessage = "Referencia requerida para este método de pago";
        isValid = false;
      }

      // Validar monto
      if (!this.payment.payment_value || this.payment.payment_value <= 0) {
        this.amountError = true;
        this.amountErrorMessage = "El monto debe ser mayor a 0";
        isValid = false;
      } else if (!this.isEfectivo && this.payment.payment_value > this.unpaidAmount) {
        // Solo marcar error si NO es efectivo
        this.amountError = true;
        this.amountErrorMessage = "El monto excede el pendiente por pagar";
        isValid = false;
      }

      // Emitir validaciones
      this.emitValidations(isValid);
      return isValid;
    },

    validateReference() {
      if (this.isEfectivo) {
        this.referenceError = false;
        this.referenceErrorMessage = "";
        return;
      }

      if (!this.payment.payment_reference?.trim()) {
        this.referenceError = true;
        this.referenceErrorMessage = "Referencia requerida para este método de pago";
      } else {
        this.referenceError = false;
        this.referenceErrorMessage = "";
      }
    },

    validatePaymentValue() {
      const value = parseFloat(this.payment.payment_value);

      if (isNaN(value) || value <= 0) {
        this.amountError = true;
        this.amountErrorMessage = "El monto debe ser mayor a 0";
      } else if (!this.isEfectivo && value > this.unpaidAmount) {
        // Solo marcar error si NO es efectivo
        this.amountError = true;
        this.amountErrorMessage = "El monto excede el pendiente por pagar";
      } else {
        this.amountError = false;
        this.amountErrorMessage = "";
      }
    },

    emitValidations(isValid) {
      const validationsCopy = [...this.validations];

      // Actualizar validaciones según el estado actual
      validationsCopy[4].status = !!this.vendor;

      // Validaciones del cliente (siempre requeridas en Colombia)
      validationsCopy[0].status = !!this.client.PartyName?.trim();
      validationsCopy[2].status = !!this.client.PartyIdentification?.trim();
      validationsCopy[3].status = !!(
        this.client.typeid?.trim() && this.client.typeid !== "GEN"
      );
      validationsCopy[7].status = !!this.client.CityName?.trim();

      validationsCopy[1].status = true; // Email siempre opcional

      this.$emit("validation-updated", {
        isValid,
        validations: validationsCopy,
      });
    },

    // Métodos de documento
    checkDocumentTypeByAmount() {
      const countryConfig = countryUtils.getCurrentCountrySettings();
      const countryCode = countryUtils.getCurrentCountryCode();

      // En Colombia, siempre Factura Electrónica
      if (countryCode === "CO") {
        this.doctype = "FE";
        this.montoRequiereFactura = false; // No hay restricciones por monto
        this.emitDocTypeChange();
        return;
      }

      // En España, lógica original con límites
      const simplifiedInvoiceLimit = countryConfig.simplifiedInvoiceLimit || 100;
      const requiresFullInvoiceLimit = countryConfig.requiresFullInvoiceLimit || 400;

      if (this.clienteSolicitaFactura) {
        this.doctype = "FC";
      } else if (this.totalAmount <= simplifiedInvoiceLimit) {
        this.doctype = "TK";
      } else if (this.totalAmount <= requiresFullInvoiceLimit) {
        this.doctype = "FS";
      } else {
        this.doctype = "FC";
      }

      this.montoRequiereFactura = this.totalAmount > requiresFullInvoiceLimit;
      this.emitDocTypeChange();
    },

    onDocTypeChange() {
      this.validateForm();
      this.emitDocTypeChange();
    },

    onFacturaRequest() {
      if (this.clienteSolicitaFactura) {
        this.doctype = "FC";
      } else {
        this.checkDocumentTypeByAmount();
      }
      this.emitDocTypeChange();
    },

    emitDocTypeChange() {
      this.$emit("doc-type-changed", {
        doctype: this.doctype,
        needsClientInfo: this.requiresClientInfo,
        montoRequiereFactura: this.montoRequiereFactura,
      });
    },

    // Métodos de pago
    onPaymentMethodChange() {
      if (this.isEfectivo) {
        this.payment.payment_reference = "";
        this.referenceError = false;
        this.referenceErrorMessage = "";
      }
      this.validateForm();
    },

    setFullAmount() {
      this.payment.payment_value = this.unpaidAmount;
      this.validatePaymentValue();
    },

    async addPayment() {
      if (!this.validateForm()) {
        this.$emit("show-message", {
          text: "Por favor, corrija los errores antes de continuar",
          color: "error",
        });
        return;
      }

      this.processingPayment = true;

      try {
        // Calcular si hay cambio
        const hasChange =
          this.isEfectivo && this.payment.payment_value > this.unpaidAmount;
        const changeAmount = hasChange
          ? this.payment.payment_value - this.unpaidAmount
          : 0;

        // Crear copia del payment para modificar
        const paymentToSend = { ...this.payment };

        // Si hay cambio, registrar el monto exacto y documentar el cambio
        if (hasChange) {
          // Guardar el valor original recibido
          const originalAmount = this.payment.payment_value;

          // Ajustar el pago al monto exacto de la factura
          paymentToSend.payment_value = this.unpaidAmount;

          // Documentar el cambio en la referencia
          paymentToSend.payment_reference = `Recibido: ${this.formatCurrency(
            originalAmount
          )} - Cambio: ${this.formatCurrency(changeAmount)}`;
        }

        const paymentData = {
          payment: paymentToSend,
          pay_mode: { ...this.pay_mode },
          vendor: this.vendor,
          client: { ...this.client },
          doctype: this.doctype,
          needsClientInfo: this.requiresClientInfo,
          isPartialPayment: paymentToSend.payment_value < this.unpaidAmount,
          remainingAmount: this.unpaidAmount - paymentToSend.payment_value,
          hasChange: hasChange,
          changeAmount: changeAmount,
          originalPaymentValue: hasChange ? this.payment.payment_value : null,
        };

        console.log("✅ Enviando pago:", paymentData);

        this.$emit("process-payment", paymentData);

        // Verificar si es pago parcial
        const isPartialPayment = paymentData.isPartialPayment;

        if (isPartialPayment) {
          // Pago parcial - preparar para el siguiente
          this.payment = createPayment();
          this.payment.payment_value = paymentData.remainingAmount;

          if (!this.isEfectivo) {
            this.payment.payment_reference = "";
          }

          this.$emit("show-message", {
            text: `Pago parcial registrado. Pendiente: ${this.formatCurrency(
              paymentData.remainingAmount
            )}`,
            color: "info",
          });
        } else {
          // Pago completo
          if (hasChange) {
            // Mostrar mensaje especial cuando hay cambio
            this.$emit("show-message", {
              text: `Pago completo. Cambio entregado: ${this.formatCurrency(
                changeAmount
              )}`,
              color: "success",
            });
          }
          // Cerrar diálogo
          setTimeout(() => {
            this.closeDialog();
          }, 500);
        }
      } catch (error) {
        console.error("Error procesando pago:", error);
        this.$emit("show-message", {
          text: "Error al procesar el pago",
          color: "error",
        });
      } finally {
        this.processingPayment = false;
      }
    },

    // Métodos de UI helpers
    openClientDialog() {
      this.$emit("open-client-dialog");
    },

    getDocumentTypeHint() {
      const countryConfig = countryUtils.getCurrentCountrySettings();
      const countryCode = countryUtils.getCurrentCountryCode();

      // En Colombia, solo Factura Electrónica
      if (countryCode === "CO") {
        return "Colombia: Todos los documentos son Factura Electrónica (FE)";
      }

      // En España, usar límites tradicionales
      const simplifiedInvoiceLimit = countryConfig.simplifiedInvoiceLimit || 100;
      const requiresFullInvoiceLimit = countryConfig.requiresFullInvoiceLimit || 400;
      const currencySymbol = countryConfig.currencySymbol || "€";

      if (this.totalAmount <= simplifiedInvoiceLimit) {
        return `Importe hasta ${simplifiedInvoiceLimit}${currencySymbol}: Recomendado ticket básico`;
      } else if (this.totalAmount <= requiresFullInvoiceLimit) {
        return `Importe entre ${simplifiedInvoiceLimit}${currencySymbol} y ${requiresFullInvoiceLimit}${currencySymbol}: Recomendada factura simplificada`;
      } else {
        return `Importe superior a ${requiresFullInvoiceLimit}${currencySymbol}: Obligatoria factura completa`;
      }
    },

    getDocTypeDescription(type) {
      const docType = this.doctypes.find((d) => d.value === type);
      return docType ? docType.description : "";
    },

    getDocTypeAlertType() {
      const countryConfig = countryUtils.getCurrentCountrySettings();
      const countryCode = countryUtils.getCurrentCountryCode();

      // En Colombia, siempre éxito (FE es único)
      if (countryCode === "CO") {
        return "success";
      }

      // En España, usar lógica original
      const requiresFullInvoiceLimit = countryConfig.requiresFullInvoiceLimit || 400;
      if (this.totalAmount > requiresFullInvoiceLimit) return "warning";
      if (this.doctype === "FC") return "info";
      return "success";
    },

    getDocTypeIcon() {
      const countryConfig = countryUtils.getCurrentCountrySettings();
      const countryCode = countryUtils.getCurrentCountryCode();

      // En Colombia, siempre check (FE es único)
      if (countryCode === "CO") {
        return "mdi-check-circle";
      }

      // En España, usar lógica original
      const requiresFullInvoiceLimit = countryConfig.requiresFullInvoiceLimit || 400;
      if (this.totalAmount > requiresFullInvoiceLimit) return "mdi-alert";
      if (this.doctype === "FC") return "mdi-information";
      return "mdi-check-circle";
    },

    getDocTypeMessage() {
      const countryConfig = countryUtils.getCurrentCountrySettings();
      const countryCode = countryUtils.getCurrentCountryCode();

      // En Colombia, mensaje específico
      if (countryCode === "CO") {
        return "Factura Electrónica: Documento fiscal válido para cualquier importe";
      }

      // En España, usar lógica original
      const requiresFullInvoiceLimit = countryConfig.requiresFullInvoiceLimit || 400;
      const currencySymbol = countryConfig.currencySymbol || "€";

      if (this.totalAmount > requiresFullInvoiceLimit) {
        return `Importe superior a ${requiresFullInvoiceLimit}${currencySymbol}: Se requiere factura completa con datos del cliente`;
      }
      if (this.doctype === "FC") {
        return "Factura completa: Permitirá al cliente deducir IVA si corresponde";
      }
      return "Documento válido para este importe";
    },

    getPaymentButtonText() {
      if (this.hasPartialPayments && this.payment.payment_value < this.unpaidAmount) {
        return "Agregar pago parcial";
      } else if (
        this.hasPartialPayments &&
        this.payment.payment_value === this.unpaidAmount
      ) {
        return "Completar pago";
      } else {
        return "Procesar pago";
      }
    },

    getPaymentStatusText() {
      if (this.hasPartialPayments) {
        // Si hay pagos previos, evaluar si este pago completa el total
        if (this.payment.payment_value === this.unpaidAmount) {
          return "Completa el pago";
        } else if (this.payment.payment_value < this.unpaidAmount) {
          return "Pago parcial";
        } else {
          return "Con cambio";
        }
      } else {
        // Si es el primer pago, evaluar contra el total
        if (this.payment.payment_value === this.totalAmount) {
          return "Pago completo";
        } else if (this.payment.payment_value < this.totalAmount) {
          return "Pago parcial";
        } else {
          return "Con cambio";
        }
      }
    },

    getPaymentStatusColor() {
      if (this.hasPartialPayments) {
        if (this.payment.payment_value === this.unpaidAmount) {
          return "success";
        } else if (this.payment.payment_value < this.unpaidAmount) {
          return "info";
        } else {
          return "warning";
        }
      } else {
        if (this.payment.payment_value === this.totalAmount) {
          return "success";
        } else if (this.payment.payment_value < this.totalAmount) {
          return "info";
        } else {
          return "warning";
        }
      }
    },

    getPaymentMethodIcon(item = null) {
      const method = item || this.pay_mode;
      if (!method) return "mdi-cash";

      const codigo = method.codigo || method.value;
      switch (codigo) {
        case "01":
          return "mdi-cash";
        case "02":
          return "mdi-credit-card";
        case "03":
          return "mdi-bank-transfer";
        case "04":
          return "mdi-cellphone";
        default:
          return "mdi-cash-multiple";
      }
    },

    getReferenceHint() {
      if (this.isEfectivo) {
        return "No requerido para efectivo";
      }
      return "Número de transacción, cheque, etc.";
    },

    getClientInitials() {
      if (!this.client.PartyName) return "?";
      return this.client.PartyName.split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },

    getClientAvatarColor() {
      if (this.isValidClientForDocType()) return "success";
      return "warning";
    },

    getClientTypeLabel() {
      if (this.client.typeid === "GEN") return "Cliente general";
      return this.getNameTypeId(this.client.typeid);
    },

    getClientStatus() {
      if (this.isValidClientForDocType()) return "Completo";
      return "Incompleto";
    },

    getClientStatusColor() {
      if (this.isValidClientForDocType()) return "success";
      return "warning";
    },

    getClientStatusTextColor() {
      return "white";
    },

    // Helpers
    getNameTypeId(code) {
      const item = this.idList.find((item) => item.code === code);
      return item ? item.name : code || "";
    },

    getNameCity(code) {
      if (!this.municipalities || !Array.isArray(this.municipalities)) {
        return code || "";
      }
      const item = this.municipalities.find((item) => item.id === code);
      return item ? item.name : code || "";
    },

    containsOnlyNumbers(str) {
      return /^\d+$/.test(str);
    },

    clearClientFields() {
      // Limpiar campos del cliente manteniendo documento y tipo de documento
      const currentDoc = this.client.PartyIdentification;
      const currentTypeId = this.client.typeid;
      this.client = {
        PartyIdentification: currentDoc,
        typeid: currentTypeId,
        PartyName: "",
        client_email: "",
        client_phone: "",
        AddressLine: "",
        CityName: this.companyData.city_code || "",
      };
    },

    searchClientByDocument() {
      if (!this.client.PartyIdentification) {
        this.$emit("show-message", {
          text: "Ingrese un número de documento para buscar",
          color: "warning",
        });
        return;
      }

      this.searchingClient = true;

      // 1. Buscar en BD local
      const qry = {
        store: window.store.store_id,
        table: "clients",
        filters: [
          {
            field: "PartyIdentification",
            operator: "=",
            value: this.client.PartyIdentification,
          },
        ],
      };

      webserver("get_dataTable", qry, (data) => {
        if (data.length > 0) {
          // ✅ Encontrado en BD local
          this.searchingClient = false;
          const foundClient = data[0];

          // Actualizar datos del cliente (mantener documento actual)
          this.client = {
            ...this.client,
            PartyName: foundClient.PartyName || "",
            typeid: foundClient.typeid || "CC",
            client_email: foundClient.client_email || "",
            client_phone: foundClient.client_phone || "",
            AddressLine: foundClient.AddressLine || "",
            CityName: foundClient.CityName || this.companyData.city_code || "",
          };

          this.$emit("show-message", {
            text: `Cliente encontrado: ${foundClient.PartyName}`,
            color: "success",
          });

          // Validar formulario
          this.$nextTick(() => {
            this.validateForm();
          });
        } else {
          // 2. No encontrado en BD local → Buscar en DIAN
          this.get_rut(
            // onSuccess - encontrado en DIAN
            (rut) => {
              this.searchingClient = false;
              this.$emit("show-message", {
                text: `Cliente encontrado en DIAN: ${rut.name}`,
                color: "success",
              });
              this.$nextTick(() => {
                this.validateForm();
              });
            },
            // onNotFound - no encontrado en DIAN
            () => {
              this.searchingClient = false;
              // Limpiar campos manteniendo solo el documento ingresado
              this.clearClientFields();
              this.$emit("show-message", {
                text: "Cliente no encontrado. Complete los datos manualmente.",
                color: "info",
              });
            }
          );
        }
      });
    },
  },
};
</script>

<style scoped>
.checkout-title {
  font-size: 24px;
  font-weight: 600;
}

.gradient-text {
  background: linear-gradient(45deg, #6772e5, #3ecf8e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

.gradient-button {
  background: linear-gradient(45deg, #6772e5, #828bec) !important;
  transition: opacity 0.3s ease;
  color: white !important;
}

.gradient-button:hover {
  opacity: 0.9;
}

.gradient-button:disabled {
  background: #e0e0e0 !important;
  color: #9e9e9e !important;
}

.customer-info {
  background-color: #f9fafc;
  border-radius: 8px;
  border-color: #e0e0e0;
  transition: border-color 0.3s ease;
}

.customer-info:hover {
  border-color: #6772e5;
}

.payment-summary {
  background-color: #f8f9fa;
  border-radius: 8px;
  border-color: #e0e0e0;
}

.payment-preview {
  background-color: #f3f4f6;
  border-radius: 8px;
  border-color: #d1d5db;
}

.v-text-field ::v-deep input,
.v-select ::v-deep .v-select__selection {
  font-size: 16px;
}

.v-btn {
  text-transform: none;
  letter-spacing: 0.5px;
}

/* Animaciones */
.dialog-fade-transition-enter-active,
.dialog-fade-transition-leave-active {
  transition: opacity 0.3s ease;
}

.dialog-fade-transition-enter,
.dialog-fade-transition-leave-to {
  opacity: 0;
}

.payment-methods-card {
  background-color: #f8f9fa;
}

.payment-method-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.payment-method-card:hover {
  border-color: #e0e0e0;
  transform: translateY(-1px);
}

.payment-method-selected {
  border-color: #1976d2 !important;
  background-color: #f3f7ff;
}

.amount-card {
  background-color: #f8f9fa;
}

.payment-preview {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border: 2px solid #e3f2fd;
}

/* Responsivo */
@media (max-width: 600px) {
  .checkout-title {
    font-size: 20px;
  }

  .gradient-button {
    width: 100%;
  }

  .payment-method-card .v-card__text {
    padding: 8px;
  }

  .payment-method-card .v-icon {
    font-size: 20px !important;
  }

  .amount-card .v-row {
    flex-direction: column;
  }

  .amount-card .v-col {
    width: 100%;
  }
}
</style>
